<template>
  <Layout>
    <PageHeader :title="pageTitle" :items="items"></PageHeader>
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="d-flex flex-wrap align-items-center mb-3">
              <b-form-select
                v-model="newsStatus"
                style="max-width: 200px"
                :options="[
                  { value: 'pending', text: 'Pending review' },
                  { value: 'approved', text: 'Posted' },
                  { value: 'rejected', text: 'Rejected' },
                  { value: 'all', text: 'All' },
                ]"
                @change="fetchNews(1)"
              ></b-form-select>

              <span v-if="newsPending" class="badge badge-danger ml-3">
                {{ newsPending }} pending
              </span>

              <div class="ml-auto">
                <button
                  class="btn btn-sm btn-light mr-2"
                  :disabled="fetchingNews"
                  @click="refreshNewsQueue"
                >
                  <i class="mdi mdi-cloud-download-outline"></i>
                  {{ fetchingNews ? "Fetching…" : "Fetch latest news" }}
                </button>
                <button class="btn btn-sm btn-light" @click="fetchNews(newsPage)">
                  <i class="mdi mdi-refresh"></i> Refresh
                </button>
              </div>
            </div>

            <p v-if="!newsItems.length" class="text-center text-muted my-5">
              Nothing here. Use “Fetch latest news” to pull new articles.
            </p>

            <div
              v-for="n in newsItems"
              :key="n._id"
              class="news-row d-flex align-items-start py-3"
            >
              <div class="flex-grow-1 pr-3">
                <a :href="n.url" target="_blank" rel="noopener" class="news-title">
                  {{ n.title }}
                </a>
                <div class="small text-muted mt-1">
                  {{ n.source }}
                  <span v-if="n.publishedAt"> · {{ n.publishedAt }}</span>
                </div>
                <div class="mt-2">
                  <span class="small text-muted mr-1">Suggested for:</span>
                  <span
                    v-for="g in n.suggestedGroups"
                    :key="g.communityId"
                    class="badge badge-light border mr-1"
                    :title="g.query"
                  >{{ g.name }}</span>
                </div>
                <div v-if="n.status !== 'pending'" class="small mt-2">
                  <span
                    class="badge"
                    :class="n.status === 'approved' ? 'badge-success' : 'badge-secondary'"
                  >{{ n.status }}</span>
                  <span v-if="n.reviewedBy" class="text-muted ml-2">
                    by {{ n.reviewedBy }}
                  </span>
                  <span v-if="n.postedTo && n.postedTo.length" class="text-muted ml-2">
                    → {{ n.postedTo.map((p) => p.name).join(", ") }}
                  </span>
                </div>
              </div>

              <div v-if="n.status === 'pending'" class="flex-none text-right">
                <button class="btn btn-sm btn-primary mb-1" @click="openPost(n)">
                  Post to groups
                </button>
                <button
                  class="btn btn-sm btn-outline-danger d-block ml-auto"
                  @click="rejectNews(n._id)"
                >
                  Reject
                </button>
              </div>
            </div>

            <div
              v-if="newsLastPage > 1"
              class="d-flex justify-content-center align-items-center mt-3"
            >
              <button
                class="btn btn-sm btn-light mr-2"
                :disabled="newsPage <= 1"
                @click="fetchNews(newsPage - 1)"
              >Prev</button>
              <span class="small text-muted">
                Page {{ newsPage }} of {{ newsLastPage }}
              </span>
              <button
                class="btn btn-sm btn-light ml-2"
                :disabled="newsPage >= newsLastPage"
                @click="fetchNews(newsPage + 1)"
              >Next</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Choose target groups -->
    <b-modal v-model="showPost" title="Post to groups" size="lg" hide-footer>
      <div v-if="active">
        <div class="font-weight-bold mb-1">{{ active.title }}</div>
        <div class="small text-muted">{{ active.source }}</div>
        <div class="small text-muted mb-3">
          Members see this posted as “{{ newsSenderName }}”
        </div>

        <b-form-group label="Add a note (optional — appears above the link)">
          <b-form-textarea
            v-model="note"
            rows="2"
            max-rows="4"
            maxlength="500"
            placeholder="e.g. Sharing for discussion —"
          ></b-form-textarea>
        </b-form-group>

        <div class="d-flex align-items-center mb-2">
          <strong>Groups</strong>
          <button class="btn btn-sm btn-link ml-auto p-0" @click="selectSuggested">
            Select suggested
          </button>
          <button class="btn btn-sm btn-link ml-3 p-0" @click="selectAllGroups">
            Select all<template v-if="newsGroupFilter"> matching</template>
          </button>
          <button class="btn btn-sm btn-link ml-3 p-0" @click="selected = []">
            Clear
          </button>
        </div>

        <b-form-input
          v-model="newsGroupFilter"
          size="sm"
          class="mb-2"
          placeholder="Search groups…"
        ></b-form-input>

        <div class="d-flex align-items-center mb-2">
          <span class="text-muted small mr-auto">
            {{ showingRange(filteredNewsGroups, newsGroupPage) }}
            · {{ selected.length }} selected
          </span>
          <b-pagination
            v-if="filteredNewsGroups.length > perPage"
            v-model="newsGroupPage"
            :total-rows="filteredNewsGroups.length"
            :per-page="perPage"
            size="sm"
            class="mb-0"
          ></b-pagination>
        </div>

        <div class="group-picker border rounded p-2">
          <p v-if="!filteredNewsGroups.length" class="text-muted small mb-0">
            No groups match that search.
          </p>
          <div
            v-for="g in pagedNewsGroups"
            :key="g.id"
            class="form-check py-1"
          >
            <input
              :id="'g-' + g.id"
              v-model="selected"
              class="form-check-input"
              type="checkbox"
              :value="g.id"
              :disabled="isAlreadyPosted(g.id)"
            />
            <label class="form-check-label" :for="'g-' + g.id">
              {{ g.name }}
              <span class="small text-muted">({{ g.type }})</span>
              <span
                v-if="isAlreadyPosted(g.id)"
                class="small text-success ml-1"
              >· already posted</span>
              <span
                v-else-if="isSuggested(g.id)"
                class="small text-primary ml-1"
              >· suggested</span>
            </label>
          </div>
        </div>

        <div class="d-flex justify-content-end mt-3">
          <button class="btn btn-light mr-2" @click="showPost = false">Cancel</button>
          <button
            class="btn btn-primary"
            :disabled="!selected.length || postingNews === active._id"
            @click="submitPost"
          >
            Post to {{ selected.length }} group(s)
          </button>
        </div>
      </div>
    </b-modal>
  </Layout>
</template>

<script>
import Layout from "@/views/layouts/main";
import PageHeader from "@/components/page-header";
import MixinRequest from "@/mixins/request";
import communityChatMixin from "@/mixins/ModuleJs/community-chat";

export default {
  data() {
    return {
      pageTitle: "News Review",
      items: [
        { text: "Home", href: "/" },
        { text: "Community Chat", href: "/community-chat" },
        { text: "News Review" },
      ],
      showPost: false,
      active: null,
      selected: [],
      note: "",
    };
  },
  mixins: [communityChatMixin, MixinRequest],
  components: { Layout, PageHeader },
  async created() {
    await this.fetchNewsGroups();
    await this.fetchNews(1);
  },
  methods: {
    openPost(item) {
      this.active = item;
      this.note = "";
      // Don't inherit the previous article's search/page in the picker.
      this.newsGroupFilter = "";
      this.newsGroupPage = 1;
      // Pre-tick the groups discovery matched, minus any already posted.
      this.selected = (item.suggestedGroups || [])
        .map((g) => g.communityId)
        .filter((id) => !this.isAlreadyPosted(id));
      this.showPost = true;
    },
    isSuggested(id) {
      return (this.active?.suggestedGroups || []).some((g) => g.communityId === id);
    },
    isAlreadyPosted(id) {
      return (this.active?.alreadyPostedTo || []).includes(id);
    },
    /**
     * Select every group the current search matches — not just the page on
     * screen — skipping any this article has already been posted to. Adds to the
     * existing selection rather than replacing it, so you can search, select,
     * search again and accumulate.
     */
    selectAllGroups() {
      const ids = this.filteredNewsGroups
        .map((g) => g.id)
        .filter((id) => !this.isAlreadyPosted(id));
      this.selected = [...new Set([...this.selected, ...ids])];
    },
    selectSuggested() {
      this.selected = (this.active?.suggestedGroups || [])
        .map((g) => g.communityId)
        .filter((id) => !this.isAlreadyPosted(id));
    },
    async submitPost() {
      const ok = await this.approveNews(this.active._id, this.selected, this.note);
      if (ok) this.showPost = false;
    },
  },
};
</script>

<style scoped>
.news-row + .news-row {
  border-top: 1px solid #eef0f5;
}
.news-title {
  font-weight: 600;
  color: #212529;
}
.news-title:hover {
  color: #3d50c4;
}
.group-picker {
  max-height: 260px;
  overflow-y: auto;
}
</style>
